import { Button } from "@frontend/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuTrigger
} from "@frontend/components/ui/dropdown-menu"
import { Input } from "@frontend/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@frontend/components/ui/table"
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState
} from "@tanstack/react-table"
import { ArrowUpDown, ChevronDown } from "lucide-react"
import * as React from "react"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  emptyMessage?: string
  filterColumnId?: string
  filterPlaceholder?: string
  showColumnToggle?: boolean
  showPagination?: boolean
  initialPageIndex?: number
  onPageChange?: (pageIndex: number) => void
  onRowClick?: (row: TData) => void
  renderSubRow?: (row: TData) => React.ReactNode
  manualPagination?: boolean
  pageCount?: number
  pageSize?: number
}

export function DataTable<TData, TValue>({
  columns,
  data,
  emptyMessage = "No results.",
  filterColumnId,
  filterPlaceholder = "Filter...",
  showColumnToggle = false,
  showPagination = true,
  initialPageIndex = 0,
  onPageChange,
  onRowClick,
  renderSubRow,
  manualPagination = false,
  pageCount: externalPageCount,
  pageSize: externalPageSize
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: externalPageSize ?? 10
  })

  // Sync pageIndex from parent when it changes (e.g. filter reset)
  React.useEffect(() => {
    setPagination((prev) => (prev.pageIndex === initialPageIndex ? prev : { ...prev, pageIndex: initialPageIndex }))
  }, [initialPageIndex])

  // Notify parent of page changes outside of render (skip initial mount)
  const onPageChangeRef = React.useRef(onPageChange)
  React.useEffect(() => {
    onPageChangeRef.current = onPageChange
  }, [onPageChange])
  const mountedRef = React.useRef(false)
  React.useEffect(() => {
    if (mountedRef.current) {
      onPageChangeRef.current?.(pagination.pageIndex)
    }
    mountedRef.current = true
  }, [pagination.pageIndex])

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    ...(manualPagination
      ? { manualPagination: true, pageCount: externalPageCount ?? -1, autoResetPageIndex: false }
      : { getPaginationRowModel: getPaginationRowModel() }),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination
    }
  })

  return (
    <div className="w-full">
      {(filterColumnId || showColumnToggle) && (
        <div className="flex items-center gap-2 py-4">
          {filterColumnId ? (
            <Input
              placeholder={filterPlaceholder}
              value={(table.getColumn(filterColumnId)?.getFilterValue() as string) ?? ""}
              onChange={(event) => table.getColumn(filterColumnId)?.setFilterValue(event.target.value)}
              className="max-w-sm"
            />
          ) : null}
          {showColumnToggle ? (
            <DropdownMenu>
              <DropdownMenuTrigger render={<Button variant="outline" className="ml-auto" />}>
                Columns <ChevronDown className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(Boolean(value))}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      )}

      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : header.column.getCanSort() ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-2 h-7"
                        onClick={() => header.column.toggleSorting(header.column.getIsSorted() === "asc")}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <ArrowUpDown className="size-3.5" />
                      </Button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    className={onRowClick ? "cursor-pointer" : undefined}
                    onClick={() => onRowClick?.(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                  {renderSubRow?.(row.original)}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {showPagination ? (
        <div className="flex items-center justify-end space-x-2 py-4">
          {table.getPageCount() > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums">
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            Next
          </Button>
        </div>
      ) : null}
    </div>
  )
}
